import { Button, Container, Group, Stack, Text, Title, Tooltip, UnstyledButton } from '@mantine/core';
import { useScrollIntoView } from '@mantine/hooks';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCreateStore } from '@/store/create';
import { EmbedListItem, FileStatus } from '@/types/embed';
import { SvgRemind, SvgTicket, SvgTip } from './icon';
import { safeDecodeURI } from '@mygpt/app';

export default () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { botId, embedList: list, toggleEmbedListOpened } = useCreateStore();

  const successLen = useMemo(() => list.filter((item) => item.file_status === 'complete').length, [list]);
  const failedLen = useMemo(() => list.filter((item) => item.file_status === 'fail').length, [list]);
  const exceededLen = useMemo(() => list.filter((item) => item.file_status === 'exceeded').length, [list]);

  const { scrollIntoView, scrollableRef, targetRef } = useScrollIntoView<HTMLLIElement, HTMLUListElement>();

  const chat = () => navigate(`/bot/${botId}/chat`);
  const retry = () => {
    window.location.reload();
  };
  const upgrade = () => {
    retry();
    window.open(`${import.meta.env.VITE_BASE_HOST}/pricing`, '_blank');
  };

  const chekcFileStatusComplete = (status: FileStatus) => {
    return status === 'complete';
  };

  const checkFileStatusFail = (status: FileStatus) => {
    return status === 'fail' || status === 'exceeded';
  };

  const FileStatusIcon = ({ item }: { item: EmbedListItem }) => {
    const { file_status: status } = item;
    if (chekcFileStatusComplete(status)) return <SvgTicket />;
    if (checkFileStatusFail(status)) return <SvgRemind />;
    return <></>;
  };

  const ActiveButton = () => {
    if (exceededLen === list.length) {
      return (
        <Button size="md" onClick={upgrade}>
          {t('pricing.content_upgrade_plan')}
        </Button>
      );
    }
    if (!successLen) {
      return (
        <Button size="md" onClick={retry}>
          {t('errors.initialize.button')}
        </Button>
      );
    }
    if (successLen && (failedLen || exceededLen)) {
      return (
        <Group justify="space-between" className="w-full">
          <Button variant="outline" size="md" onClick={upgrade}>
            {t('pricing.content_upgrade_plan')}
          </Button>
          <Button size="md" onClick={chat}>
            {t('data_check.start_chatting')}
          </Button>
        </Group>
      );
    }
    return (
      <Button size="md" onClick={chat}>
        {t('data_check.start_chatting')}
      </Button>
    );
  };

  useEffect(() => {
    return () => {
      toggleEmbedListOpened();
    };
  }, []);

  return (
    <Container>
      <Stack mt={40} gap={40}>
        <section>
          <section className="rounded-[20px] bg-white border border-[#F8F6FF] pt-5">
            <section className="px-5">
              <Title order={3}>{t('create_v2.create_embed.sub_title')}</Title>
            </section>
            <ul className="mt-5 max-h-[35vh] overflow-y-auto" ref={scrollableRef}>
              {list
                .sort((a, b) => {
                  if (chekcFileStatusComplete(a.file_status)) {
                    return -1;
                  } else {
                    return 1;
                  }
                })
                .map((item, index) => {
                  return (
                    <li
                      key={index}
                      className="px-5 py-2 hover:bg-gray-100"
                      ref={chekcFileStatusComplete(item.file_status) ? targetRef : null}
                    >
                      <Group justify="space-between" wrap="nowrap">
                        <span className="flex-none flex items-center">
                          <FileStatusIcon item={item} />
                        </span>
                        <div className="flex-auto align-text-bottom min-w-0 break-all">
                          <Tooltip
                            color="gray.6"
                            label={item.file_type === 'html' ? safeDecodeURI(item.filename) : item.filename}
                            withinPortal={false}
                            multiline
                          >
                            {item.file_type === 'html' ? (
                              <Text
                                component="a"
                                href={item.filename}
                                target="_blank"
                                rel="noreferrer"
                                color="gray.5"
                                size={'sm'}
                                lineClamp={1}
                              >
                                {safeDecodeURI(item.filename)}
                              </Text>
                            ) : (
                              <span>{item.filename}</span>
                            )}
                          </Tooltip>
                          {checkFileStatusFail(item.file_status) && (
                            <Text className="text-[13px]" color="red.5">
                              {`Failed${item.failed_reason ? ` - ${item.failed_reason}` : ''}`}
                            </Text>
                          )}
                        </div>
                        {chekcFileStatusComplete(item.file_status) && (
                          <span className="flex-none flex items-center gap-2 text-sm">
                            <span className={`inline-block w-2 h-2 rounded-full bg-[#7C68FB]`} />
                            <span className="text-[#7C68FB]">{t('create_v2.create_embed.learned')}</span>
                          </span>
                        )}
                        {checkFileStatusFail(item.file_status) && (
                          // <ErrorTooltip item={item}>
                          <span className="flex-none flex items-center gap-2 text-sm cursor-default">
                            <span className={`inline-block w-2 h-2 rounded-full bg-[#667085]`} />
                            <span className="text-[#667085]">{t('create_v2.create_embed.unlearned')}</span>
                          </span>
                          // </ErrorTooltip>
                        )}
                      </Group>
                    </li>
                  );
                })}
            </ul>
          </section>
          <Group justify="flex-end" mt={10}>
            <SvgTip />
            <UnstyledButton onClick={() => scrollIntoView()}>
              <Text size={'md'} td="underline">
                {t('create_v2.create_embed.sum_learn', { successLen: successLen, failedLen: failedLen + exceededLen })}
              </Text>
            </UnstyledButton>
          </Group>
        </section>
        <Group justify="center">
          <ActiveButton />
        </Group>
      </Stack>
    </Container>
  );
};
